import { Button } from '@mui/material'
import { Row } from '@tanstack/react-table'
import { useSnackbar } from 'notistack'
import { useState } from 'react'
import { FieldErrors } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'

import Dialog from 'components/Dialog'
import { Switch } from 'components/Switch'
import { updateUser, useAuthUser } from 'features/user'
import { Workspace } from 'features/workspace'
import { mapGraphQLErrors } from 'providers/graphql'
import { useMutation } from 'providers/tanstack-query/useMutation'
import { WorkspaceForTable } from '../../types'
import { isWorkspaceRow } from '../utils'
import { UPDATE_WORKSPACE_STATUS } from './mutation'
import { Data, Variables, WorkspaceSliceDTO } from './types'

export const StatusSwitch = ({ depth, original: { active, id, name } }: Row<WorkspaceForTable>) => {
  const { t } = useTranslation('features/workspace')
  const { user } = useAuthUser()
  const dispatch = useDispatch()
  const { enqueueSnackbar } = useSnackbar()
  const [checked, setChecked] = useState<boolean>(active)
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false)

  const { isPending: loading, mutateAsync: update } = useMutation<{ input: Variables }, Data>({
    mutation: UPDATE_WORKSPACE_STATUS,
    onCompleted: async (data: Data) => {
      const {
        updateWorkspaceStatus: { errors, workspace },
      } = data

      setIsDialogOpen(false)

      if (errors && errors.length > 0) {
        const err: FieldErrors<WorkspaceSliceDTO> = mapGraphQLErrors<WorkspaceSliceDTO>(errors)
        const msg = err.active?.message

        if (msg !== undefined) {
          enqueueSnackbar(msg as string, { variant: 'error' })
        }
      } else if (workspace !== null) {
        setChecked(workspace.active)
        enqueueSnackbar(t('statusSwitch.successUpdate'), { variant: 'success' })

        const updatedWorkspaces = user.workspaces.map((w: Workspace) =>
          w.id === id
            ? {
                ...w,
                active: !checked,
              }
            : w,
        )
        dispatch(updateUser({ workspaces: updatedWorkspaces }))
      }
    },
    queriesToInvalidate: ['getWorkspacesForTable', 'getFreshWorkspaceAlerts'],
  })

  const handleUpdate = () =>
    void update({
      input: {
        active: !checked,
        id,
      },
    })

  const selector = checked ? 'deactivate' : 'activate'

  const DialogAction: JSX.Element = (
    <Button
      disabled={loading}
      onClick={handleUpdate}
      variant='contained'
    >
      {t(`statusSwitch.dialog.${selector}.action`)}
    </Button>
  )

  return isWorkspaceRow(depth) ? (
    <>
      <Switch
        checked={checked}
        name={`workspace-${id}-active`}
        onClick={() => setIsDialogOpen(true)}
      />

      <Dialog
        actions={DialogAction}
        content={t(`statusSwitch.dialog.${selector}.content`, { name })}
        isOpen={isDialogOpen}
        setOpen={setIsDialogOpen}
        title={t(`statusSwitch.dialog.${selector}.title`)}
      />
    </>
  ) : (
    <></>
  )
}
